import React, { useState } from 'react';
import { X, FileText, Download, ExternalLink, RefreshCw, AlertTriangle, ShieldCheck, CheckCircle2, Clock, Ban } from 'lucide-react';
import { DocumentoFiscal } from '../../types/fiscal';
import { fiscalService } from '../../services/fiscalService';

interface DetalhesDocumentoFiscalModalProps {
  documento: DocumentoFiscal | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function DetalhesDocumentoFiscalModal({
  documento,
  isOpen,
  onClose,
  onUpdate,
}: DetalhesDocumentoFiscalModalProps) {
  const [loadingConsulta, setLoadingConsulta] = useState(false);
  const [loadingCancelamento, setLoadingCancelamento] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [mensagem, setMensagem] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);

  if (!isOpen || !documento) return null;

  const isCte = documento.modelo === '57' || documento.modelo === '67';
  const isNfse = documento.modelo === 'NFS-e';

  const handleConsultar = async () => {
    setLoadingConsulta(true);
    setMensagem(null);
    try {
      let res;
      if (isCte) {
        res = await fiscalService.consultarCte(documento.empresa_id, documento.id);
      } else if (isNfse) {
        res = await fiscalService.consultarNfse(documento.empresa_id, documento.id);
      } else {
        throw new Error('Modelo não suporta consulta automática.');
      }

      if (res.sucesso) {
        setMensagem({
          tipo: 'sucesso',
          texto: `Status consultado: ${res.status || 'atualizado'}. ${res.mensagem || ''}`,
        });
        if (onUpdate) onUpdate();
      } else {
        setMensagem({ tipo: 'erro', texto: res.mensagem || 'Falha ao consultar órgão autorizador.' });
      }
    } catch (e: any) {
      setMensagem({ tipo: 'erro', texto: e.message });
    } finally {
      setLoadingConsulta(false);
    }
  };

  const handleCancelar = async () => {
    if (justificativa.length < 15) {
      alert('A justificativa de cancelamento perante a SEFAZ exige no mínimo 15 caracteres.');
      return;
    }

    setLoadingCancelamento(true);
    setMensagem(null);
    try {
      let res;
      if (isCte) {
        res = await fiscalService.cancelarCte(documento.empresa_id, documento.id, justificativa);
      } else {
        res = await fiscalService.cancelarNfse(documento.empresa_id, documento.id, justificativa);
      }

      if (res.sucesso) {
        setMensagem({
          tipo: 'sucesso',
          texto: `Documento cancelado com sucesso. Protocolo: ${res.focus?.protocolo_cancelamento || 'Registrado'}`,
        });
        setShowCancelModal(false);
        if (onUpdate) onUpdate();
      } else {
        setMensagem({ tipo: 'erro', texto: res.mensagem || 'Rejeição ao solicitar cancelamento.' });
      }
    } catch (e: any) {
      setMensagem({ tipo: 'erro', texto: e.message });
    } finally {
      setLoadingCancelamento(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'autorizado':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
            <CheckCircle2 className="w-3.5 h-3.5" /> Autorizado
          </span>
        );
      case 'processando':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">
            <Clock className="w-3.5 h-3.5 animate-spin" /> Em Processamento
          </span>
        );
      case 'cancelado':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-200 text-slate-700 border border-slate-300">
            <Ban className="w-3.5 h-3.5" /> Cancelado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-300">
            <AlertTriangle className="w-3.5 h-3.5" /> {status}
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-white">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold">
                  {isCte ? 'CT-e' : isNfse ? 'NFS-e' : 'Documento Fiscal'} #{documento.numero || documento.id}
                </h2>
                <span className="text-xs px-2 py-0.5 rounded bg-slate-800 font-mono text-slate-300">
                  Série {documento.serie || '1'}
                </span>
              </div>
              <p className="text-xs text-slate-400">
                {documento.empresa?.razao_social || 'Empresa Emissora'} • Ref: {documento.provedor_ref || 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {getStatusBadge(documento.status)}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1 text-sm text-slate-800">
          {/* Alertas */}
          {mensagem && (
            <div
              className={`p-4 rounded-xl text-xs flex items-center gap-2 ${
                mensagem.tipo === 'sucesso'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  : 'bg-rose-50 border border-rose-200 text-rose-800'
              }`}
            >
              {mensagem.tipo === 'sucesso' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}
              <span>{mensagem.texto}</span>
            </div>
          )}

          {/* Chave e Protocolo */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-2xs font-bold text-slate-500 uppercase tracking-wider block">
                  Chave de Acesso
                </span>
                <span className="font-mono text-xs font-bold text-slate-900 break-all select-all">
                  {documento.chave_acesso || 'Ainda não gerada / Em processamento'}
                </span>
              </div>
              {documento.chave_acesso && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(documento.chave_acesso || '');
                    alert('Chave copiada para a área de transferência!');
                  }}
                  className="px-2.5 py-1 text-2xs font-semibold bg-white border border-slate-300 rounded hover:bg-slate-100 cursor-pointer self-start sm:self-auto"
                >
                  Copiar Chave
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-200 text-xs">
              <div>
                <span className="text-2xs text-slate-500 block">Protocolo SEFAZ:</span>
                <span className="font-mono font-semibold">{documento.protocolo_autorizacao || 'Pendente'}</span>
              </div>
              <div>
                <span className="text-2xs text-slate-500 block">Ambiente:</span>
                <span className="font-bold uppercase text-slate-700">{documento.ambiente}</span>
              </div>
              <div>
                <span className="text-2xs text-slate-500 block">Valor Total:</span>
                <span className="font-bold text-emerald-700">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(documento.valor_total || 0)}
                </span>
              </div>
            </div>

            {documento.motivo_status && (
              <div className="pt-2 border-t border-slate-200 text-xs">
                <span className="text-2xs text-slate-500 block">Mensagem do Fisco / Provedor:</span>
                <span className="text-slate-700 italic">{documento.motivo_status}</span>
              </div>
            )}
          </div>

          {/* Links e Documentos Oficiais */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase">Arquivos e Impressão Oficial</h3>
            <div className="flex flex-wrap gap-3">
              {documento.pdf_url ? (
                <a
                  href={documento.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold shadow-sm"
                >
                  <FileText className="w-4 h-4" />
                  Visualizar {isCte ? 'DACTE' : 'DANFSE'} (PDF)
                  <ExternalLink className="w-3.5 h-3.5 ml-1 opacity-70" />
                </a>
              ) : (
                <button
                  disabled
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-400 rounded-lg text-xs font-bold cursor-not-allowed"
                >
                  <FileText className="w-4 h-4" />
                  PDF ainda não disponível
                </button>
              )}

              {documento.xml_url ? (
                <a
                  href={documento.xml_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold shadow-sm"
                >
                  <Download className="w-4 h-4 text-slate-500" />
                  Download XML Assinado
                </a>
              ) : null}

              <button
                type="button"
                onClick={handleConsultar}
                disabled={loadingConsulta}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-bold shadow-sm cursor-pointer ml-auto"
              >
                <RefreshCw className={`w-4 h-4 text-blue-600 ${loadingConsulta ? 'animate-spin' : ''}`} />
                {loadingConsulta ? 'Consultando...' : 'Consultar na SEFAZ'}
              </button>
            </div>
          </div>

          {/* Modal de Cancelamento Embutido */}
          {showCancelModal && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl space-y-3 animate-in fade-in">
              <div className="flex items-center gap-2 text-rose-800 font-bold text-xs">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                Cancelamento de Documento Fiscal perante a SEFAZ
              </div>
              <p className="text-2xs text-rose-700">
                Atenção: Esta ação é irreversível e transmitirá o pedido de cancelamento formal diretamente à SEFAZ/Prefeitura.
              </p>
              <div>
                <label className="block text-2xs font-semibold text-slate-700 mb-1">
                  Justificativa (mínimo 15 caracteres):
                </label>
                <input
                  type="text"
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  placeholder="Ex: Cancelamento decorrente de erro no preenchimento de rota"
                  className="w-full bg-white border border-rose-300 rounded-lg px-3 py-2 text-xs"
                />
                <span className="text-2xs text-slate-400 mt-1 block">
                  {justificativa.length}/15 caracteres mínimos
                </span>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold"
                >
                  Desistir
                </button>
                <button
                  type="button"
                  onClick={handleCancelar}
                  disabled={loadingCancelamento || justificativa.length < 15}
                  className="px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                >
                  {loadingCancelamento ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Transmitindo cancelamento...
                    </>
                  ) : (
                    <>
                      <Ban className="w-3.5 h-3.5" />
                      Confirmar Cancelamento
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="bg-slate-100 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <div>
            {documento.status === 'autorizado' && !showCancelModal && (
              <button
                type="button"
                onClick={() => setShowCancelModal(true)}
                className="px-3 py-2 text-xs font-bold text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-200 transition-colors cursor-pointer"
              >
                Solicitar Cancelamento
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
